import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/axios";
import FormHeader from "@/components/FormHeader";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useState } from "react";
import { Loader2 } from "lucide-react";
import toast from "react-hot-toast";
import { AxiosError } from "axios";
import { User, VerIjazahKelompok } from "@/types";
import Combobox from "@/components/Combobox";
import InputError from "@/components/InputError";
import { getListUser } from "@/services/getData";
import { FaUserCheck } from "react-icons/fa6";

const patchDataAssignVerifikator = async (newData: {
  id_verijazah_kelompok: string;
  user_verifikator1: string;
  user_verifikator2?: string;
  user_verifikator3?: string;
}) => {
  const response = await api.patch(
    "/setting/verifikator-ijazah/asign-user",
    newData,
  );
  return response.data;
};

export default function Form({
  closeModal,
  selectedRow,
}: {
  closeModal: () => void;
  selectedRow: VerIjazahKelompok | null;
}) {
  // 1. Pisahkan state pencarian untuk masing-masing combobox
  const [searchQuery1, setSearchQuery1] = useState("");
  const [searchQuery2, setSearchQuery2] = useState("");
  const [searchQuery3, setSearchQuery3] = useState("");

  const [openListVerifikator, setOpenListVerifikator] = useState(false);
  const [openListVerifikator2, setOpenListVerifikator2] = useState(false);
  const [openListVerifikator3, setOpenListVerifikator3] = useState(false);

  const [form, setForm] = useState({
    id_verijazah_kelompok: selectedRow?.id_verijazah_kelompok || "",
    user_verifikator1: "",
    user_verifikator2: "",
    user_verifikator3: "",
  });

  // 2. Buat query terpisah untuk masing-masing combobox agar datanya independen
  const { data: dataVerifikator1, isPending: isPending1 } = useQuery({
    queryKey: ["listVerifikator", "verif1", searchQuery1],
    queryFn: () => getListUser(searchQuery1, 10, "verifikator_raport"),
    select: (data) =>
      data?.data?.map((user: User) => ({
        label: user.nama_lengkap,
        value: `${user.id}`,
      })),
  });

  const { data: dataVerifikator2, isPending: isPending2 } = useQuery({
    queryKey: ["listVerifikator", "verif2", searchQuery2],
    queryFn: () => getListUser(searchQuery2, 10, "verifikator_raport"),
    select: (data) =>
      data?.data?.map((user: User) => ({
        label: user.nama_lengkap,
        value: `${user.id}`,
      })),
  });

  const { data: dataVerifikator3, isPending: isPending3 } = useQuery({
    queryKey: ["listVerifikator", "verif3", searchQuery3],
    queryFn: () => getListUser(searchQuery3, 10, "verifikator_raport"),
    select: (data) =>
      data?.data?.map((user: User) => ({
        label: user.nama_lengkap,
        value: `${user.id}`,
      })),
  });

  const queryClient = useQueryClient();
  const { mutate, isPending, error } = useMutation({
    mutationFn: patchDataAssignVerifikator,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["listKelompokVerifikasiIjazah"],
      });
      closeModal();
      toast.success(data?.message || "Success.");
    },
    onError: (
      error: AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>,
    ) => {
      const err = error as AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>;
      const errorMessage =
        err.response?.data?.message || "An unexpected error occurred.";
      toast.error(errorMessage);
    },
  });

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    mutate(form);
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
      <FormHeader
        icon={<FaUserCheck />}
        title={`Daftarkan Verifikator ke kelompok ${selectedRow?.kelompok}`}
        description="Mendaftarkan verifikator ke kelompok yang dipilih."
      />

      {/* Verifikator 1 */}
      <div>
        <Label>Verifikator 1</Label>
        <Combobox
          serverSide
          open={openListVerifikator}
          setOpen={setOpenListVerifikator}
          value={form.user_verifikator1}
          setValue={(newValue) => {
            setForm((prev) => ({
              ...prev,
              user_verifikator1: newValue,
            }));
          }}
          options={dataVerifikator1}
          placeholder="Pilih Verifikator"
          onChange={(event) => setSearchQuery1(event.target.value)}
          searchQueryValue={searchQuery1 || ""}
          isPending={isPending1}
        />
        <InputError
          text={error?.response?.data?.errors?.user_verifikator1?.join(",")}
        />
      </div>

      {/* Verifikator 2 */}
      <div>
        <Label>Verifikator 2</Label>
        <Combobox
          serverSide
          open={openListVerifikator2}
          setOpen={setOpenListVerifikator2}
          value={form.user_verifikator2}
          setValue={(newValue) => {
            setForm((prev) => ({
              ...prev,
              user_verifikator2: newValue,
            }));
          }}
          options={dataVerifikator2}
          placeholder="Pilih Verifikator"
          onChange={(event) => setSearchQuery2(event.target.value)}
          searchQueryValue={searchQuery2 || ""}
          isPending={isPending2}
        />
        <InputError
          text={error?.response?.data?.errors?.user_verifikator2?.join(",")}
        />
      </div>

      {/* Verifikator 3 */}
      <div>
        <Label>Verifikator 3</Label>
        <Combobox
          serverSide
          open={openListVerifikator3}
          setOpen={setOpenListVerifikator3}
          value={form.user_verifikator3}
          setValue={(newValue) => {
            setForm((prev) => ({
              ...prev,
              user_verifikator3: newValue,
            }));
          }}
          options={dataVerifikator3}
          placeholder="Pilih Verifikator"
          onChange={(event) => setSearchQuery3(event.target.value)}
          searchQueryValue={searchQuery3 || ""}
          isPending={isPending3}
        />
        <InputError
          text={error?.response?.data?.errors?.user_verifikator3?.join(",")}
        />
      </div>

      <div className="mt-3 flex gap-3">
        <Button
          type="button"
          variant={"outline"}
          disabled={isPending}
          onClick={() => closeModal()}
        >
          Batal
        </Button>
        <Button className="flex items-center gap-3" disabled={isPending}>
          {isPending ? (
            <>
              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              Processing...
            </>
          ) : (
            "Daftarkan"
          )}
        </Button>
      </div>
    </form>
  );
}
